import { useRef, useState } from 'react';
import { LifeBuoy, Plus, Send, Trash2 } from 'lucide-react';
import { TICKET_PRIORITY_LABELS, type CreateTicketInput, type Ticket, type Workspace } from '@unitly/contracts';
import {
  useAddTicketComment, useCreateTicket, useDeleteTicket, useEnableTickets, useTicketComments, useTicketConfig, useTickets, useUpdateTicket,
  useWorkspace,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { Sheet } from '../../ui/Sheet.tsx';
import { Avatar, EmptyState, PageLoader, Segmented } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { relativeTime } from '../../lib/dates.ts';

const PRIORITY_COLORS = ['var(--label-3)', 'var(--blue)', 'var(--orange)', 'var(--red)'];

export function TicketsView({ workspace }: { workspace: Workspace }) {
  const { orgId } = useOrgCtx();
  const config = useTicketConfig(orgId, workspace.id);
  const [status, setStatus] = useState<'open' | 'closed' | 'all'>('open');
  const tickets = useTickets(orgId, workspace.id, { status });
  const [editing, setEditing] = useState<Ticket | null>(null);
  const [creating, setCreating] = useState(false);
  const newSeq = useRef(0);

  if (config.isLoading) return <PageLoader />;
  const cfg = config.data;
  if (!cfg?.enabled) return <EnableTickets workspace={workspace} canEnable={Boolean(cfg?.permissions.admin)} />;

  return (
    <>
      <div className="toolbar">
        <Segmented label="Stato" value={status} onChange={setStatus} options={[{ value: 'open', label: 'Aperti' }, { value: 'closed', label: 'Chiusi' }, { value: 'all', label: 'Tutti' }]} />
        <span className="spacer" />
        {cfg.permissions.create && <button type="button" className="btn btn-primary btn-sm" onClick={() => { newSeq.current += 1; setCreating(true); }}><Plus size={16} /> Ticket</button>}
      </div>
      {tickets.isLoading && <PageLoader />}
      {tickets.data?.length === 0 && <div className="card"><EmptyState tone="neutral" title="Nessun ticket">Apri un ticket per chiedere supporto a questo spazio.</EmptyState></div>}
      {tickets.data && tickets.data.length > 0 && (
        <div className="card list">
          {tickets.data.map((t) => (
            <button key={t.id} type="button" className="list-row" onClick={() => setEditing(t)}>
              <span className="list-icon" style={{ background: PRIORITY_COLORS[t.priority] }}><LifeBuoy size={15} /></span>
              <span className="grow">
                <div>{t.key} · {t.subject}</div>
                <div className="secondary">{t.requestedBy.displayName} · {relativeTime(t.createdAt)}{t.assignee ? ` · assegnato a ${t.assignee.displayName}` : ''}</div>
              </span>
              <span className="pill" data-cat={t.state.category}>{t.state.name}</span>
            </button>
          ))}
        </div>
      )}
      <TicketFormSheet key={`new-${newSeq.current}`} workspace={workspace} open={creating} onClose={() => setCreating(false)} />
      {editing && <TicketDetailSheet workspace={workspace} ticket={editing} states={cfg.states} onClose={() => setEditing(null)} />}
    </>
  );
}

function EnableTickets({ workspace, canEnable }: { workspace: Workspace; canEnable: boolean }) {
  const { orgId } = useOrgCtx();
  const enable = useEnableTickets(orgId, workspace.id);
  const toast = useToast();
  return (
    <div className="card card-pad" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 28 }}>
      <span className="empty" style={{ padding: 0 }}><span className="icon" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}><LifeBuoy size={22} /></span></span>
      <strong style={{ fontSize: 18 }}>Ticket di {workspace.name}</strong>
      <p style={{ margin: 0, color: 'var(--label-2)', maxWidth: '46ch', fontSize: 15 }}>
        Chiunque nell’organizzazione può aprire un ticket in questo spazio; qui il team lo assegna, discute e risolve.
      </p>
      {canEnable ? (
        <button type="button" className="btn btn-primary" disabled={enable.isPending}
          onClick={() => enable.mutate(undefined, { onSuccess: () => toast('Ticket attivati'), onError: (e) => toast(e.message) })}>
          Attiva i ticket
        </button>
      ) : (
        <p className="hint">Chiedi a chi gestisce lo spazio di attivarli.</p>
      )}
    </div>
  );
}

function TicketFormSheet({ workspace, open, onClose }: { workspace: Workspace; open: boolean; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const create = useCreateTicket(orgId, workspace.id);
  const wsMembers = useWorkspace(orgId, workspace.id);
  const toast = useToast();
  const [form, setForm] = useState<CreateTicketInput>({ subject: '', description: '', priority: 1, assigneeId: null });

  return (
    <Sheet open={open} onClose={onClose} variant="center" title="Nuovo ticket" labelledBy="ticket-title"
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          <span className="spacer" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={!form.subject?.trim() || create.isPending}
            onClick={() => create.mutate(form, { onSuccess: onClose, onError: (e) => toast(e instanceof ApiError ? e.message : 'Creazione non riuscita') })}>
            Apri ticket
          </button>
        </div>
      }>
      <label className="field"><span>Oggetto</span><input className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="es. Non riesco ad accedere" data-autofocus /></label>
      <label className="field"><span>Descrizione</span><textarea className="textarea" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
      <div className="field">
        <span>Priorità</span>
        <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) as CreateTicketInput['priority'] })}>
          {Object.entries(TICKET_PRIORITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="field">
        <span>Assegna a (opzionale)</span>
        <select className="input" value={form.assigneeId ?? ''} onChange={(e) => setForm({ ...form, assigneeId: e.target.value || null })}>
          <option value="">—</option>
          {wsMembers.data?.members.map((m) => <option key={m.userId} value={m.userId}>{m.displayName}</option>)}
        </select>
      </div>
    </Sheet>
  );
}

function TicketDetailSheet({ workspace, ticket, states, onClose }: { workspace: Workspace; ticket: Ticket; states: { id: string; name: string }[]; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const update = useUpdateTicket(orgId, workspace.id);
  const del = useDeleteTicket(orgId, workspace.id);
  const wsMembers = useWorkspace(orgId, workspace.id);
  const comments = useTicketComments(orgId, workspace.id, ticket.id);
  const addComment = useAddTicketComment(orgId, workspace.id, ticket.id);
  const toast = useToast();
  const [comment, setComment] = useState('');

  return (
    <Sheet open onClose={onClose} variant="side" full labelledBy="ticket-detail-title">
      <div className="toolbar" style={{ marginBottom: 4 }}>
        <span className="pill" data-cat={ticket.state.category}>{ticket.state.name}</span>
        <span className="spacer" />
        {ticket.permissions.delete && (
          <button type="button" className="icon-btn muted" aria-label="Elimina ticket" onClick={() => { if (confirm('Eliminare questo ticket?')) del.mutate(ticket.id, { onSuccess: onClose }); }}>
            <Trash2 size={18} />
          </button>
        )}
      </div>
      <h2 id="ticket-detail-title" style={{ margin: '0 0 4px' }}>{ticket.key} · {ticket.subject}</h2>
      <p className="secondary" style={{ marginTop: 0 }}>Aperto da {ticket.requestedBy.displayName} · {relativeTime(ticket.createdAt)}</p>
      {ticket.description && <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</p>}

      {ticket.permissions.edit && (
        <div style={{ display: 'flex', gap: 10, margin: '14px 0' }}>
          <div className="field" style={{ flex: 1 }}>
            <span>Fase</span>
            <select className="input" value={ticket.state.id} onChange={(e) => update.mutate({ id: ticket.id, patch: { stateId: e.target.value } }, { onError: (err) => toast(err instanceof ApiError ? err.message : 'Operazione non riuscita') })}>
              {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <span>Assegnatario</span>
            <select className="input" value={ticket.assignee?.id ?? ''} onChange={(e) => update.mutate({ id: ticket.id, patch: { assigneeId: e.target.value || null } })}>
              <option value="">—</option>
              {wsMembers.data?.members.map((m) => <option key={m.userId} value={m.userId}>{m.displayName}</option>)}
            </select>
          </div>
        </div>
      )}

      <hr className="sep" />
      <h3 className="subhead">Commenti</h3>
      <div className="list" style={{ marginBottom: 10 }}>
        {comments.data?.map((c) => (
          <div key={c.id} className="list-row" style={{ cursor: 'default' }}>
            <Avatar id={c.author.id} name={c.author.displayName} size={30} />
            <span className="grow">
              <div className="secondary">{c.author.displayName} · {relativeTime(c.createdAt)}</div>
              <div>{c.body}</div>
            </span>
          </div>
        ))}
        {comments.data?.length === 0 && <p className="hint" style={{ padding: 12, margin: 0 }}>Nessun commento ancora.</p>}
      </div>
      {ticket.permissions.comment && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input className="input" style={{ flex: 1 }} placeholder="Scrivi un commento…" value={comment} onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && comment.trim()) { addComment.mutate(comment, { onSuccess: () => setComment('') }); } }} />
          <button type="button" className="icon-btn" aria-label="Invia commento" disabled={!comment.trim()} onClick={() => addComment.mutate(comment, { onSuccess: () => setComment('') })}>
            <Send size={17} />
          </button>
        </div>
      )}
    </Sheet>
  );
}
